import { useEffect, useState } from "react";
import axios from "axios";
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from "react-share";
import useFooddyStore from "../../store";
import { Card, CardHead, Icon, OutlineButton, PrimaryButton, Segmented, rupees } from "./parts";
import { avatarAt } from "./avatars";

// Invite code saved by the /invitation/:username/:invitecode page.
const cookieInviteCode = () => {
  for (const cookie of document.cookie.split(";")) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith("invitecode=")) return decodeURIComponent(trimmed.substring("invitecode=".length));
  }
  return null;
};

const copyText = (text) => {
  if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
  return Promise.resolve();
};

// Invite friends with a referral code, accept someone else's code, and see
// who joined (same backend calls as the previous InviteContainer).
function Friends({ userId, platformName, total, userName }) {
  const { baseUrl, inviteCode, setInviteCode, friends, setFriends } = useFooddyStore();
  const [tab, setTab] = useState("invite");
  const [copied, setCopied] = useState(false);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!userId) return;
    axios
      .post(`${baseUrl}/api/invitation/invitecode`, { mapUserId: userId })
      .then((response) => response?.data && setInviteCode(response.data.invitationCode))
      .catch(() => {});
    axios
      .post(`${baseUrl}/api/invitation/invitors`, { mapUserId: userId })
      .then((response) => response?.data && setFriends(response.data))
      .catch(() => {});
    const fromCookie = cookieInviteCode();
    if (fromCookie) {
      axios
        .post(`${baseUrl}/api/invitation/inviteWithCode`, { invitationCode: fromCookie, accepterId: userId })
        .catch(() => {});
    }
  }, [userId]);

  useEffect(() => {
    if (!status) return;
    const timer = setTimeout(() => setStatus(null), 3000);
    return () => clearTimeout(timer);
  }, [status]);

  const copy = () => {
    copyText(inviteCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    try {
      const response = await axios.post(`${baseUrl}/api/invitation/inviteWithCode`, {
        invitationCode: code,
        accepterId: userId,
      });
      // The API answers with an error message string, or an object on success.
      if (typeof response?.data === "string") setStatus({ ok: false, text: response.data });
      else if (response?.data) setStatus({ ok: true, text: "Invitation accepted" });
    } catch (error) {
      setStatus({ ok: false, text: "Could not use that code" });
    }
  };

  const shareUrl = `https://app.ecombullet.com/invitation/${userName}/${inviteCode}`;
  const message = `Just tallied up my ${platformName.toLowerCase()} online ecom expenses: a whopping ${rupees(
    total
  )}! 💸 I was shocked to see my spending add up like this. 😅 Have you checked yours? Let's compare! Connect with my code ${inviteCode}`;

  return (
    <Card id="friends">
      <CardHead>
        <span>Challenge your friends</span>
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: "invite", label: "Invite" },
            { value: "joined", label: `Joined · ${friends.length}` },
          ]}
        />
      </CardHead>

      {tab === "invite" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 12.5, color: "var(--ink2)" }}>
            Share your code. Friends who join show up here and you can compare spends.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div
              style={{
                flex: 1,
                height: 36,
                padding: "0 12px",
                borderRadius: 9,
                border: "1px dashed var(--acc-mid)",
                background: "var(--acc-soft)",
                display: "flex",
                alignItems: "center",
                font: "600 14px 'IBM Plex Mono', monospace",
                letterSpacing: ".06em",
                color: "var(--acc)",
              }}
            >
              {inviteCode || "······"}
            </div>
            <PrimaryButton onClick={copy} disabled={!inviteCode}>
              <Icon name={copied ? "check" : "content_copy"} size={16} weight={400} />
              {copied ? "Copied" : "Copy"}
            </PrimaryButton>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--ink3)", flex: 1 }}>Share via</span>
            <WhatsappShareButton url={shareUrl} title={message}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <FacebookShareButton url={shareUrl} quote={message} hashtag="#Ecom">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={message} hashtags={["ecom", "amazon", "flipkart", "spending_calculator"]}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {friends.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {friends.map((friend, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px 6px 6px", borderRadius: 20, background: "var(--surface2)" }}>
                  <img src={avatarAt(friend.gender, i)} alt="" style={{ width: 26, height: 26, borderRadius: "50%" }} />
                  <span style={{ fontSize: 12.5, fontWeight: 500 }}>{friend.user_name || "Friend"}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: "var(--ink2)" }}>No friends have joined with your code yet.</div>
          )}
          <form onSubmit={submit} style={{ display: "flex", gap: 8 }}>
            <input
              value={code}
              placeholder="Have a code? Enter it here"
              onChange={(event) => {
                setCode(event.target.value);
                setStatus(null);
              }}
              style={{
                flex: 1,
                height: 36,
                padding: "0 12px",
                border: "1px solid var(--line)",
                borderRadius: 9,
                background: "var(--surface2)",
                outline: "none",
                fontSize: 13,
              }}
            />
            <OutlineButton type="submit" style={{ height: 36 }} disabled={!code.trim()}>
              Submit
            </OutlineButton>
          </form>
          {status && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: status.ok ? "var(--good)" : "var(--bad)" }}>
              <Icon name={status.ok ? "check_circle" : "error"} size={16} fill weight={400} />
              {status.text}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default Friends;
